import React, { useRef } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { getConnectProvider } from "../../agent/connectProviders";
import { openMockConnectPopup } from "../../agent/mockConnectPopup";

// In-chat integration connect.
//
// The provider registry (agent/connectProviders.js) reuses the SAME connect
// mechanism the settings pages use, so this card performs the real flow
// without navigating away from the conversation.
//
// The scripted demo popup — which reports "connected" after ~2.6s without
// contacting any provider — now survives only behind an explicit
// sg_copilot_mock_oauth = "1" flag. It used to be the DEFAULT, which meant the
// agent was told an integration was live when nothing had been connected.
const MOCK_OAUTH_FLAG = "sg_copilot_mock_oauth";

const isMockForced = () => {
  try {
    return localStorage.getItem(MOCK_OAUTH_FLAG) === "1";
  } catch (e) {
    return false;
  }
};

const ConnectIntegrationBlock = observer(({ block }) => {
  const busyRef = useRef(false);
  const provider = getConnectProvider(block.provider);
  const label = block.label || `Connect ${provider ? provider.label : block.provider}`;

  const reportResult = (status, data) => {
    busyRef.current = false;
    copilotStore.sendActionResult(block.id, block.actionId, status, data);
  };

  const handleConnect = () => {
    if (block.status === "connected" || copilotStore.streaming || busyRef.current) {
      return;
    }
    busyRef.current = true;

    if (isMockForced() || !provider || !provider.isConfigured()) {
      // No provider wired for this environment (missing OAuth URL in the build
      // config) — fall back to the scripted popup rather than a dead button.
      openMockConnectPopup({
        label: provider ? provider.label : block.provider,
        iconUrl: provider ? new URL(provider.icon, window.location.origin).href : "",
        onDone: () => reportResult("success", { mock: true })
      });
      return;
    }

    try {
      provider.connect(result => {
        // LinkedIn hands off to a multi-step popup with no single success
        // callback, and the OAuth providers report the same way when their
        // popup is blocked or closed early: report "dismissed" so the agent
        // re-checks the connection instead of assuming it succeeded. Either
        // way reportResult clears busyRef, so the button stays clickable.
        if (result && result.deferred) {
          reportResult("dismissed", { deferred: true });
          return;
        }
        reportResult("success");
      });
    } catch (e) {
      // Never leave the button latched on a throw — busyRef is only cleared by
      // reportResult, so without this the card is dead until a page reload.
      reportResult("failed", { error: String(e && e.message) });
    }
  };

  const connected = block.status === "connected";

  return (
    <div className={`copilotConnect status-${block.status}`}>
      <div className="copilotConnect__icon">
        {provider && <img src={provider.icon} alt={`Connect ${provider.label}`} />}
      </div>
      <div className="copilotConnect__meta">
        <div className="copilotConnect__label">{label}</div>
        {block.description && (
          <div className="copilotConnect__desc">{block.description}</div>
        )}
      </div>
      {connected ? (
        <div className="copilotConnect__connected">
          <i className="material-icons-outlined">check_circle</i>
          <span>Connected</span>
        </div>
      ) : (
        <button
          type="button"
          className="copilotConnect__btn"
          disabled={copilotStore.streaming}
          onClick={handleConnect}
        >
          {block.status === "failed" ? "Retry connection" : label}
        </button>
      )}
    </div>
  );
});

export default ConnectIntegrationBlock;
